import React, { useState } from 'react';
import { Form, FormCheck, FormControl, FormGroup, FormLabel } from 'react-bootstrap';

import { Regions } from '../../constants/states';
import { useFleet } from '../../hooks/useFleet';
import { useInput } from '../../hooks/useInput';
import { EventType } from '../../interfaces';
import { MatchMode, PostPermittedPlate, PriviledgedGroup } from '../../interfaces/whitelisted_plate';
import { invalidatePermittedPlates, useBackwardsCompatiblePlates, usePermittedPlatesGroups } from '../../queries';
import { PermittedPlatesService } from '../../services';
import { getDefaultState, humanizeEnum } from '../../utils';
import { matchModeLabels } from './matchModeLabels';
import { Alert } from '../atoms/alert';
import { Button } from '../atoms/button';
import { ButtonTag } from '../atoms/buttonTag';
import { ButtonsContainer } from '../atoms/buttonsContainer';
import { H3, H4 } from '../atoms/headings';
import { Hr } from '../atoms/hr';
import { Modal } from '../atoms/modal';
import { Padded } from '../atoms/padded';
import { ProgressButton } from '../atoms/progressButton';
import { EventTypesFilterBrowser } from '../filter/EventTypesFilterBrowser';

interface Props {
  toggle: () => void;
  plate?: PostPermittedPlate | null;
}

export function AddPlateModal(props: Props) {
  const { toggle } = props;
  const [isSaving, setSaving] = useState(false);
  const fleet = useFleet();
  const { data } = useBackwardsCompatiblePlates();
  const exceptions = data ?? [];
  const groups = usePermittedPlatesGroups();
  const [selectedEventTypes, setSelectedEventTypes] = useState<EventType[]>([]);

  const MODAL_STYLE = { minWidth: '40vw' };

  const { bind: plate } = useInput<string>(props.plate?.license_plate || '');
  const { bind: state } = useInput<string>(props.plate?.state_code || getDefaultState().abbr);
  const { bind: group } = useInput<string>(props.plate?.categorization_code || '');
  const { bind: matchMode } = useInput<MatchMode>(props.plate?.match_mode || 'exact');

  const save = (evt: any): boolean => {
    evt.preventDefault();
    evt.stopPropagation();

    const newPlate: PostPermittedPlate = {
      permitted_plate_id: props.plate?.permitted_plate_id,
      license_plate: plate.value.toUpperCase(),
      state_code: state.value.toUpperCase(),
      categorization_code: group.value as PriviledgedGroup,
      event_type_identifiers: selectedEventTypes.map((et) => et.identifier),
      match_mode: matchMode.value,
    };

    if (!newPlate.categorization_code) {
      new Alert('Please select a privileged group.').alert();
      return false;
    }

    if (
      exceptions.some(
        (e) =>
          e.license_plate.toUpperCase() === newPlate.license_plate.toUpperCase() &&
          e.state_code.toUpperCase() === newPlate.state_code.toUpperCase() &&
          e.match_mode === newPlate.match_mode,
      )
    ) {
      new Alert(
        `License ${newPlate.license_plate} (${newPlate.state_code}, ${matchModeLabels[newPlate.match_mode ?? 'exact']} match) is already on the permitted list!`,
      ).alert();
      return false;
    }

    setSaving(true);

    // edit existing one
    PermittedPlatesService.createOrUpdate(newPlate, fleet.uuid)
      .then(() => {
        invalidatePermittedPlates(fleet.uuid);
        setSaving(false);
        props.toggle();
      })
      .finally(() => {
        setSaving(false);
      });

    return false;
  };

  const removeEventType = (eventType: EventType) => () => {
    if (!eventType.id) {
      return;
    }
    setSelectedEventTypes((types) => types.filter((t) => t.id !== eventType.id));
  };

  return (
    <Modal isOpen onToggle={toggle} data-test="addPlateModal" contentLabel="License plate exception" style={MODAL_STYLE}>
      <Padded>
        <H3>{props.plate ? 'Edit a ' : 'Add a new '}license plate exception</H3>
        <H4>* Indicates a required field</H4>
      </Padded>

      <Hr />

      <Form onSubmit={save}>
        <Padded>
          <FormGroup>
            <div style={{ display: 'flex', gap: '16px' }}>
              <div>
                <FormLabel>Plate matching</FormLabel>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', height: '38px' }}>
                  {Object.entries(matchModeLabels).map(([value, label]) => (
                    <FormCheck
                      key={value}
                      type="radio"
                      id={`match-mode-${value}`}
                      name="matchMode"
                      label={label}
                      value={value}
                      checked={matchMode.value === value}
                      onChange={matchMode.onChange}
                      inline
                    />
                  ))}
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <FormLabel>License plate number*</FormLabel>
                <FormControl
                  {...plate}
                  placeholder="e.g., HEO-001, IAJ-999"
                  autoFocus
                  required
                  minLength={3}
                  style={{ textTransform: plate.value.length > 0 ? 'uppercase' : 'none' }}
                />
              </div>
            </div>
          </FormGroup>

          <FormGroup>
            <FormLabel>State*</FormLabel>
            <FormControl as="select" custom required {...state}>
              <option value="" disabled>Please select</option>
              {Regions.map((r) => (
                <optgroup label={r.label} key={r.label}>
                  {r.states.map((s) => (
                    <option value={s.region} key={s.region}>
                      {s.state}
                    </option>
                  ))}
                </optgroup>
              ))}
            </FormControl>
          </FormGroup>

          <FormGroup>
            <FormLabel>Limit Exception to these event types only</FormLabel>
            <EventTypesFilterBrowser
              selectedEventTypes={selectedEventTypes}
              setSelectedEventTypes={setSelectedEventTypes}
              required={selectedEventTypes.length === 0}
              allowAll
            />

            {selectedEventTypes?.map((et: EventType) => (
              <ButtonTag key={et.id} onClick={removeEventType(et)}>
                {humanizeEnum(et.identifier)} ×
              </ButtonTag>
            ))}
          </FormGroup>

          <FormGroup>
            <FormLabel>Privileged group</FormLabel>
            <FormControl {...group} placeholder="Privileged group" required minLength={3} list="groups" />

            <datalist id="groups">
              {groups.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </datalist>
          </FormGroup>

          <ButtonsContainer>
            <ProgressButton variant="primary" block type="submit" data-accesskey="Enter" data-disableKey isLoading={isSaving}>
              Save
            </ProgressButton>

            <Button variant="secondary" onClick={toggle} block>
              Cancel
            </Button>
          </ButtonsContainer>
        </Padded>
      </Form>
    </Modal>
  );
}
